import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Plus, MoreHorizontal, Check, X, Pencil, Trash2, Power, Eye} from 'lucide-react'
import { useI18n } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { resolveEntityTypeIcon } from '@/components/entity-type/entity-type-icon'
import { resolveEntityTypeColorVariant } from '@/components/entity-type/entity-type-badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {TypeValuesView} from '@/components/types/TypeValuesView'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {CreateTypeValueRequestFields} from '@/types/types'
import type {
  TypeValue,
  CreateTypeValueRequest,
  UpdateTranslationsRequest,
} from '@/types/types'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {LargeDialog} from '@/components/shared/LargeDialog'
import {PageLayout} from '@/components/shared/PageLayout'
import {useServices} from '@/services/context'
import {cn} from '@/lib/utils'

export function TypeDetail() {
    const services = useServices()
  const { tableName } = useParams<{ tableName: string }>()
  const i18n = useI18n()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const addDialog = useDialog()
  const viewDialog = useDialog()
  const editDialog = useDialog()
  const deleteDialog = useDialog()
  const [selectedValue, setSelectedValue] = useState<TypeValue | null>(null)
  const [newCode, setNewCode] = useState('')
  const [translations, setTranslations] = useState<Record<string, { label: string; description: string }>>({})

  const canView = hasPermission(permissions.types.view)
  const canCreate = hasPermission(permissions.types.create)
  const canUpdate = hasPermission(permissions.types.update)
  const canDelete = hasPermission(permissions.types.delete)

  // Fetch locales
  const { data: localesData } = useQuery({
    queryKey: ['types', 'locales'],
    queryFn: () => services.types.locales(),
    enabled: canView,
  })

  // Fetch type tables list (for entityType flag)
  const { data: typeTables = [] } = useQuery({
    queryKey: ['types'],
    queryFn: () => services.types.list(),
    enabled: canView,
  })

  // Check if this table is an entity type
  const summary = typeTables.find((t) => t.tableName === tableName)
  const isEntityType = summary?.entityType ?? false
  const hasUsageTracking = summary?.hasUsageTracking ?? true

  // Fetch type table
  const { data: typeTable, isLoading, error } = useQuery({
    queryKey: ['types', tableName],
    queryFn: () => services.types.detail(tableName!),
    enabled: !!tableName && canView,
  })

  // Create mutation
  const createMutation = useEntityMutation({
    mutationFn: (data: CreateTypeValueRequest) =>
      services.types.create(tableName!, data),
    invalidate: [['types'], ['app-config']],
    closeDialog: addDialog.close,
    onSuccess: () => {
      resetForm()
    },
  })

  // Update translations mutation
  const updateTranslationsMutation = useEntityMutation({
    mutationFn: ({ code, data }: { code: string; data: UpdateTranslationsRequest }) =>
      services.types.updateTranslations(tableName!, code, data),
    invalidate: [['types'], ['app-config']],
    closeDialog: editDialog.close,
    onSuccess: () => {
      setSelectedValue(null)
    },
  })

  // Toggle active mutation
  const toggleActiveMutation = useEntityMutation({
    mutationFn: ({ code, active }: { code: string; active: boolean }) =>
      services.types.toggleActive(tableName!, code, active),
    invalidate: [['types'], ['app-config']],
  })

  // Delete mutation
  const deleteMutation = useEntityMutation({
    mutationFn: (code: string) =>
      services.types.remove(tableName!, code),
    invalidate: [['types'], ['app-config']],
    closeDialog: deleteDialog.close,
    onSuccess: () => {
      setSelectedValue(null)
    },
  })

  // eslint-disable-next-line i18next/no-literal-string
  const locales = localesData?.locales || ['en']

  const resetForm = () => {
    setNewCode('')
    setTranslations({})
  }

  const openAddDialog = () => {
    resetForm()
    const initialTranslations: Record<string, { label: string; description: string }> = {}
    locales.forEach(locale => {
      initialTranslations[locale] = { label: '', description: '' }
    })
    setTranslations(initialTranslations)
    addDialog.open()
  }

  const openViewDialog = (value: TypeValue) => {
    setSelectedValue(value)
    viewDialog.open()
  }

  const openEditDialog = (value: TypeValue) => {
    setSelectedValue(value)
    const editTranslations: Record<string, { label: string; description: string }> = {}
    locales.forEach(locale => {
      const t = value.translations[locale]
      editTranslations[locale] = {
        label: t?.label || '',
        description: t?.description || '',
      }
    })
    setTranslations(editTranslations)
    editDialog.open()
  }

  const openDeleteDialog = (value: TypeValue) => {
    setSelectedValue(value)
    deleteDialog.open()
  }

  const handleCreate = () => {
    const maxSortOrder = typeTable?.values.reduce((max, v) => Math.max(max, v.sortOrder), 0) || 0
    createMutation.mutate({
      code: newCode.toUpperCase().replace(/\s+/g, '_'),
      sortOrder: maxSortOrder + 1,
      translations: translations as Record<string, { label: string; description: string | null }>,
    })
  }

  const handleUpdateTranslations = () => {
    if (!selectedValue) return
    updateTranslationsMutation.mutate({
      code: selectedValue.code,
      data: {
        translations: translations as Record<string, { label: string; description: string | null }>,
      },
    })
  }

  const handleToggleActive = (value: TypeValue) => {
    toggleActiveMutation.mutate({ code: value.code, active: !value.active })
  }

  const handleDelete = () => {
    if (!selectedValue) return
    deleteMutation.mutate(selectedValue.code)
  }

  if (!canView) {
    return (
      <Stack className="p-4">
        <MutedText>{i18n.types.noPermission}</MutedText>
      </Stack>
    )
  }

  return (
    <PageLayout
      data-testid="type-detail-page"
      variant="detail"
      title={typeTable?.displayName ?? i18n.types.detailPageTitle}
      description={typeTable?.tableName}
      icon={CONCEPT_ICON.typeValue}
      loading={isLoading}
      error={error ?? (!isLoading ? notFoundIfMissing(typeTable, 'TYPE') : undefined)}
      backTo={routes.schema.types.list()}
      actions={typeTable && canCreate ? (
        <Button onClick={openAddDialog} data-testid="btn-add-value">
          <IconText icon={Plus}>{i18n.types.addValue}</IconText>
        </Button>
      ) : undefined}
    >
      {typeTable && <>
      {/* Values Table */}
      <TypeValuesView
        values={typeTable.values}
        locales={locales}
        isEntityType={isEntityType}
        hasUsageTracking={hasUsageTracking}
        renderRow={(value) => renderValueRow({
          value,
          isEntityType,
          hasUsageTracking,
          tableName: tableName!,
          canUpdate,
          canDelete,
          navigate,
          onView: openViewDialog,
          onEdit: openEditDialog,
          onToggleActive: handleToggleActive,
          onDelete: openDeleteDialog,
          i18n,
        })}
      />

      {/* Add Dialog */}
      <Dialog open={addDialog.isOpen} onOpenChange={addDialog.bind.onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{i18n.types.addTypeValue}</DialogTitle>
            <DialogDescription>
              {i18n.types.createValueDescription({ name: typeTable.displayName })}
            </DialogDescription>
          </DialogHeader>
          <FormGrid className="py-4">
            <FormField htmlFor={CreateTypeValueRequestFields.code} label={i18n.types.code}>
              {/* eslint-disable i18next/no-literal-string */}
              <Input
                id={CreateTypeValueRequestFields.code}
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                placeholder="MY_TYPE_VALUE"
                className="font-mono"
              />
              {/* eslint-enable i18next/no-literal-string */}
              <MutedText size="xs">
                {i18n.types.codeFormatHint}
              </MutedText>
            </FormField>
            {locales.map(locale => (
              <Stack key={locale} gap="sm">
                <Label className="capitalize">{i18n.types.translationLabel({ locale })}</Label>
                <Input
                  value={translations[locale]?.label || ''}
                  onChange={(e) => setTranslations(prev => ({
                    ...prev,
                    [locale]: { ...prev[locale], label: e.target.value }
                  }))}
                  placeholder={i18n.types.label}
                />
                <Input
                  value={translations[locale]?.description || ''}
                  onChange={(e) => setTranslations(prev => ({
                    ...prev,
                    [locale]: { ...prev[locale], description: e.target.value }
                  }))}
                  placeholder={i18n.types.descriptionOptional}
                />
              </Stack>
            ))}
          </FormGrid>
          <SaveCancelFooter
            testIdPrefix="add"
            onCancel={addDialog.close}
            saving={createMutation.isPending}
            saveLabel={i18n.common.create}
            savingLabel={i18n.common.creating}
            disabled={!newCode || locales.some(l => !translations[l]?.label)}
            saveType="button"
            onSave={handleCreate}
          />
        </DialogContent>
      </Dialog>

      {/* View Translations Dialog */}
      <Dialog open={viewDialog.isOpen} onOpenChange={viewDialog.bind.onOpenChange}>
        <LargeDialog>
          <DialogHeader>
            <DialogTitle>{i18n.types.translations}</DialogTitle>
            <DialogDescription>
              {i18n.types.translationsFor({ code: selectedValue?.code ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead data-testid="col-language" className="w-20">{i18n.types.language}</TableHead>
                <TableHead data-testid="col-label">{i18n.types.label}</TableHead>
                <TableHead data-testid="col-description">{i18n.common.description}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locales.map(locale => {
                const t = selectedValue?.translations[locale]
                return (
                  <TableRow key={locale}>
                    <TableCell className="font-medium uppercase">{locale}</TableCell>
                    { }
                    <TableCell>{t?.label || <MutedText className="italic">{'\u2014'}</MutedText>}</TableCell>
                    { }
                    <TableCell>{t?.description || <MutedText className="italic">{'\u2014'}</MutedText>}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <DialogFooter>
            <Button variant="outline" onClick={viewDialog.close} data-testid="btn-close-view">
              {i18n.common.close}
            </Button>
            {canUpdate && (
              <Button onClick={() => {
                viewDialog.close()
                if (selectedValue) openEditDialog(selectedValue)
              }} data-testid="btn-edit-translations">
                <IconText icon={Pencil}>{i18n.common.edit}</IconText>
              </Button>
            )}
          </DialogFooter>
        </LargeDialog>
      </Dialog>

      {/* Edit Translations Dialog */}
      <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
        <LargeDialog>
          <DialogHeader>
            <DialogTitle>{i18n.types.editTranslations}</DialogTitle>
            <DialogDescription>
              {i18n.types.editTranslationsFor({ code: selectedValue?.code ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead data-testid="col-language-edit" className="w-20">{i18n.types.language}</TableHead>
                <TableHead data-testid="col-label-edit">{i18n.types.label}</TableHead>
                <TableHead data-testid="col-description-edit">{i18n.common.description}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locales.map(locale => (
                <TableRow key={locale}>
                  <TableCell className="font-medium uppercase">{locale}</TableCell>
                  <TableCell>
                    <Input
                      value={translations[locale]?.label || ''}
                      onChange={(e) => setTranslations(prev => ({
                        ...prev,
                        [locale]: { ...prev[locale], label: e.target.value }
                      }))}
                      placeholder={i18n.types.label}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={translations[locale]?.description || ''}
                      onChange={(e) => setTranslations(prev => ({
                        ...prev,
                        [locale]: { ...prev[locale], description: e.target.value }
                      }))}
                      placeholder={i18n.types.descriptionOptional}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <SaveCancelFooter
            testIdPrefix="edit"
            onCancel={editDialog.close}
            saving={updateTranslationsMutation.isPending}
            disabled={locales.some(l => !translations[l]?.label)}
            saveType="button"
            onSave={handleUpdateTranslations}
          />
        </LargeDialog>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n.types.deleteTypeValue}</DialogTitle>
            <DialogDescription>
              {selectedValue?.usageCount && selectedValue.usageCount > 0 ? (
                <>
                  {i18n.types.cannotDelete({ code: selectedValue?.code ?? '', count: selectedValue?.usageCount ?? 0 })}
                  <br /><br />
                  {i18n.types.setInactiveHint}
                </>
              ) : (
                i18n.types.deleteValueConfirm({ code: selectedValue?.code ?? '' })
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={deleteDialog.close} data-testid="btn-cancel-delete">
              {i18n.common.cancel}
            </Button>
            {selectedValue?.usageCount === 0 && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                data-testid="btn-confirm-delete"
              >
                {deleteMutation.isPending ? i18n.common.deleting : i18n.common.delete}
              </Button>
            )}
            {selectedValue?.usageCount && selectedValue.usageCount > 0 && canUpdate && (
              <Button
                variant="secondary"
                onClick={() => {
                  handleToggleActive(selectedValue)
                  deleteDialog.close()
                }}
                data-testid="btn-set-inactive"
              >
                {i18n.types.setInactive}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>}
    </PageLayout>
  )
}

interface RenderRowArgs {
  value: TypeValue
  isEntityType: boolean
  hasUsageTracking: boolean
  tableName: string
  canUpdate: boolean
  canDelete: boolean
  navigate: ReturnType<typeof useNavigate>
  onView: (v: TypeValue) => void
  onEdit: (v: TypeValue) => void
  onToggleActive: (v: TypeValue) => void
  onDelete: (v: TypeValue) => void
  i18n: ReturnType<typeof useI18n>
}

function renderValueRow(args: RenderRowArgs) {
  const {value, isEntityType, hasUsageTracking, tableName, canUpdate, canDelete, navigate, onView, onEdit, onToggleActive, onDelete, i18n} = args
  const settings = value.settings as Record<string, unknown> | null
  const iconName = settings && typeof settings.icon === 'string' ? settings.icon : null
  const colorName = settings && typeof settings.color === 'string' ? settings.color : null
  const EntityIcon = isEntityType ? resolveEntityTypeIcon(iconName) : null
  const colorVariant = isEntityType ? resolveEntityTypeColorVariant(colorName) : undefined
  const rowClass = cn(
      !value.active && 'opacity-50',
      isEntityType && 'cursor-pointer hover:bg-accent/50',
  )
  return (
    <TableRow
      key={value.code}
      className={rowClass}
      onClick={isEntityType ? () => navigate(routes.schema.types.valueDetail(tableName, value.code)) : undefined}
    >
      {isEntityType && (
        <TableCell className="w-10">
          {EntityIcon && <Icon icon={EntityIcon} size="md" muted/>}
        </TableCell>
      )}
      <TableCell className="font-mono">{value.code}</TableCell>
      {hasUsageTracking && (
        <TableCell className="text-center">
          <Badge variant={isEntityType ? colorVariant : (value.usageCount > 0 ? 'secondary' : 'outline')}>
            {value.usageCount}
          </Badge>
        </TableCell>
      )}
      <TableCell className="text-center" data-testid={`active-cell-${value.code}`} data-active={value.active}>
        {value.active ? (
          <Icon icon={Check} size="md" className="mx-auto text-status-success" aria-label={i18n.common.active}/>
        ) : (
          <Icon icon={X} size="md" className="mx-auto" muted aria-label={i18n.common.inactive}/>
        )}
      </TableCell>
      <TableCell>
        {isEntityType ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid={`btn-actions-${value.code}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(value)}>
              <IconText icon={Eye}>{i18n.types.viewTranslations}</IconText>
            </DropdownMenuItem>
            {canUpdate && (
              <>
                <DropdownMenuItem onClick={() => onEdit(value)}>
                  <IconText icon={Pencil}>{i18n.types.editTranslations}</IconText>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleActive(value)} data-testid={`btn-toggle-active-${value.code}`}>
                  <IconText icon={Power}>{value.active ? i18n.types.setInactive : i18n.types.setActive}</IconText>
                </DropdownMenuItem>
              </>
            )}
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete(value)}
                  disabled={value.usageCount > 0}
                >
                  <IconText icon={Trash2}>{i18n.common.delete}{value.usageCount > 0 && ` ${i18n.types.inUse({ count: value.usageCount })}`}</IconText>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  )
}
